import { defineMetrics } from "../types.js"

/**
 * ⚠ That srv dashboard has a third component, `getApplicationsSubmitted` (a `trackingAssociated`
 * exists and the associated tracking's `status ∈ {GOVERNMENT, NEED_MORE_INFORMATION}`). It is
 * **not** the third slice of this split: Submitted can hold at the same time as Approved or
 * In Progress — it is a different axis. Putting it in the same pie makes the slices add up to
 * more than 100%, so only the two mutually exclusive, exhaustive categories are emitted here.
 */
export const applicationsMetrics = defineMetrics([
  {
    id: "total_applications",
    name: "Total Applications",
    description: "Total number of Applications.",
    logic: "Count all Application records.",
    source: "application.countDocuments()",
    domain: "applications",
    srvModule: "applications",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["line", "bar"],
    coveredByV1: true,
    xlsxRow: 32,
  },
  {
    id: "application_creation_date",
    name: "Application Creation Date",
    description: "Date an application was created.",
    logic: "Retrieve application creation timestamp.",
    source: "application.createdAt, one point per application",
    domain: "applications",
    srvModule: "applications",
    priority: "must",
    valueType: "timestamp",
    dims: ["entity"],
    rollup: "last",
    defaultWidget: "table",
    coveredByV1: false,
    xlsxRow: 33,
  },
  {
    id: "approval_date",
    name: "Approval Date",
    description: "Date an application reached Approved status.",
    logic: "Retrieve approval timestamp.",
    source: "max(application.sections[].approvedDate), only for fully approved applications",
    domain: "applications",
    srvModule: "applications",
    priority: "must",
    valueType: "timestamp",
    dims: ["entity"],
    rollup: "last",
    defaultWidget: "table",
    coveredByV1: false,
    caveat:
      "srv stores no single approval timestamp. An application is approved only when every section of its wizard snapshot is approved, so this is the latest sections[].approvedDate among those, and it is absent for applications that are not fully approved. Verified against srv's own getApplicationsApproved widget in S-4.",
    xlsxRow: 34,
  },
  {
    id: "application_status",
    name: "Application Status",
    description: "Current workflow status of an application.",
    logic: "Retrieve current status field.",
    source: "derived: approvedSections == wizardSnapshot.sections -> approved, else in_progress",
    domain: "applications",
    srvModule: "applications",
    priority: "must",
    valueType: "count",
    dims: ["class"],
    rollup: "last",
    defaultWidget: "pie",
    altWidgets: ["stacked-bar", "hbar", "table"],
    coveredByV1: false,
    caveat:
      "Application has no status field. These two classes are computed the way srv's own applications dashboard computes them: all wizard-snapshot sections approved means approved, anything else means in_progress. srv's third widget, Submitted, is deliberately not a third slice — it can be true at the same time as either class, so the pie would exceed 100%.",
    xlsxRow: 35,
  },
  {
    id: "associated_country",
    name: "Associated Country",
    description: "Country linked to an application.",
    logic: "Retrieve country associated with the application.",
    source: "application.country, the raw alpha-3 code",
    domain: "applications",
    srvModule: "applications",
    priority: "must",
    valueType: "count",
    dims: ["country"],
    rollup: "last",
    defaultWidget: "map",
    altWidgets: ["hbar", "table"],
    coveredByV1: false,
    caveat:
      "The value is the alpha-3 code srv stores, passed through unchanged rather than mapped to a country name; the map widget needs the code anyway, and every translation is a chance to be wrong. Applications with no country set emit nothing rather than an unknown bucket.",
    xlsxRow: 36,
  },
  {
    id: "total_cloned_applications",
    name: "Total Cloned Applications",
    description: "Number of applications created through cloning.",
    logic: "Count applications where source application exists.",
    source: "application.cloneRef exists and is non-null — there is no boolean clone flag",
    domain: "applications",
    srvModule: "applications",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["line", "bar"],
    coveredByV1: true,
    xlsxRow: 37,
  },
])
